import { fetchExperienceCategories } from 'actions/ExperienceActions'
import type { FilterPanelCheckItem } from 'components/Common/FilterPanel/FilterPanelCheckboxGroup'
import FilterPanelCheckboxGroup from 'components/Common/FilterPanel/FilterPanelCheckboxGroup'
import FilterPanelFilterGroup from 'components/Common/FilterPanel/FilterPanelFilterGroup'
import { EmptyArray, groupBy, sortBy, sum, without } from 'lib/array/arrayUtils'
import { useCallback, useEffect, useMemo, useRef } from 'react'
import * as React from 'react'
import { useIntl } from 'react-intl'
import { connect } from 'react-redux'
import { useAppDispatch } from 'hooks/reduxHooks'
import useExperiencesSearchFilters from 'components/Experiences/hooks/useExperiencesSearchFilters'
import Group from 'components/utils/Group'
import { AnythingCategory, EXP_CATEGORY_LUX_PLUS_ID, EXP_CATEGORY_LUX_PLUS_MEMBER_PRICES_ID } from 'constants/experience'
import LuxPlusLabel from 'LuxKit/Label/LuxPlusLabel'
import { getIsEnabledLuxPlusDiscountedExperiences } from 'luxPlus/selectors/featureToggle'
import FilterChip from 'LuxKit/Chips/FilterChip'
import DropdownList from 'LuxKit/Dropdown/List/DropdownList'
import LineAngleDownIcon from 'LuxKit/Icons/line/LineAngleDownIcon'
import LineAngleUpIcon from 'LuxKit/Icons/line/LineAngleUpIcon'
import BodyText from 'LuxKit/Typography/BodyText'
import useToggle from 'hooks/useToggle'
import CSSBreakpoint from 'components/utils/CSSBreakpoint'
import LayoutContainer from 'components/Common/LayoutContainer'
import styled from 'styled-components'
import zIndex from 'styles/tools/z-index'
import { useInView } from 'react-intersection-observer'
import { rem } from 'polished'
import LoadingAnimation from 'components/Common/Loading/LoadingAnimation'
import useMostPopulatedSearchExperienceFacets from '../hooks/useMostPopulatedSearchExperienceFacets'

const HeaderContainer = styled.div`
  position: sticky;
  z-index: ${zIndex.searchSubMenu};
  background: ${props => props.theme.palette.neutral.default.eight};
  width: 100%;
  top: 0;
  margin: 0 auto;

  &.lifted {
    box-shadow: 0 4px 4px 0 rgba(0, 0, 0, 0.05);
  }
`

const FiltersContainer = styled.div`
  display: flex;
  flex-wrap: wrap;
  gap: ${rem(8)};
`

interface FilterDropdownProps {
  label: string
  items: Array<FilterPanelCheckItem>
  onFilterChange: (e: React.ChangeEvent<HTMLInputElement>) => void
  onReset: () => void
  isFetching?: boolean
}
function FilterDropdown({ label, items, onFilterChange, onReset, isFetching = false }: FilterDropdownProps) {
  const intl = useIntl()
  const [isDropdownOpen, toggleDropDown, , closeDropdown] = useToggle()
  const filterChipRef = useRef<HTMLButtonElement>(null)

  const selectedCount = items.filter(item => item.checked).length
  const chipLabel = label + (selectedCount ? ` (${selectedCount})` : '')

  const onClose = useCallback(() => {
    closeDropdown()
  }, [closeDropdown])

  const hasAvailableFilters = items.some(item => !item.hidden)

  return (
    <div>
      <FilterChip
        ref={filterChipRef}
        selected={!!selectedCount}
        size="medium"
        onClick={toggleDropDown}
        endIcon={isDropdownOpen ? <LineAngleUpIcon /> : <LineAngleDownIcon />}
      >
        {chipLabel}
      </FilterChip>
      <CSSBreakpoint min="tablet">
        <DropdownList
          size="M"
          triggerRef={filterChipRef}
          anchorRef={filterChipRef}
          open={isDropdownOpen}
          placement="bottom-start"
          onClose={onClose}
          secondaryActionProps={{
            kind: 'tertiary',
            children: intl.formatMessage({
              id: '61b-YHMXB-JsuWKpgENc',
              defaultMessage: 'Reset',
              description: 'Button text to reset filter selections in experience search map filter dropdown',
            }),
            onClick: onReset,
          }}
        >
          <LayoutContainer>
            <FilterPanelFilterGroup allowToggle={false} showDropdown={false} title={label} showBorder={false}>
              {isFetching && (
                <Group direction="vertical" horizontalAlign="center" verticalAlign="center" gap={8}>
                  <LoadingAnimation size={16} />
                </Group>
              )}
              {!isFetching && !hasAvailableFilters && <BodyText variant="medium">
                {intl.formatMessage({
                  id: 'dagJf2WFiyWNfetEbv29',
                  defaultMessage: 'No available categories',
                  description: 'Empty state message when no categories are available for filtering in experience search map',
                })}
              </BodyText>}
              {!isFetching && hasAvailableFilters && <FilterPanelCheckboxGroup
                name={label}
                items={items}
                onChange={onFilterChange}
                maxItems={5}
                allowShowMore={false}
              />}
            </FilterPanelFilterGroup>
          </LayoutContainer>
        </DropdownList>
      </CSSBreakpoint>
    </div>
  )
}

interface CategoryItem {
  category: Omit<App.ExperienceItemCategory, 'name'> & { name: string | React.ReactNode }
  count: number
  items: Array<FilterPanelCheckItem>
  isLuxPlus?: boolean
}

interface CategoryFilterDropdownProps {
  categoryItem: CategoryItem
  onCategoryChange: (e: React.ChangeEvent<HTMLInputElement>) => void
  onResetCategory: (item: CategoryItem) => void
  isFetching?: boolean
}

function CategoryFilterDropdown({ categoryItem, onCategoryChange, onResetCategory, isFetching = false }: CategoryFilterDropdownProps) {
  const isLuxPlus = categoryItem.category.id === EXP_CATEGORY_LUX_PLUS_ID
  const categoryName = typeof categoryItem.category.name === 'string' ? categoryItem.category.name : isLuxPlus ? 'LuxPlus' : 'Other Categories'

  const onReset = useCallback(() => {
    onResetCategory(categoryItem)
  }, [categoryItem, onResetCategory])

  return (
    <FilterDropdown
      label={categoryName}
      items={categoryItem.items}
      onFilterChange={onCategoryChange}
      onReset={onReset}
      isFetching={isFetching}
    />
  )
}

interface MappedProps {
  categories: Array<App.ExperienceItemCategory>
  isLuxPlusEnabled: boolean
}

interface Props {
  place?: App.Place
  experiences?: Array<App.ExperienceOffer>
  isFetching?: boolean
}

function ExperienceSearchMapFilters(props: Props & MappedProps) {
  const {
    place,
    isLuxPlusEnabled,
    categories = EmptyArray as Array<App.ExperienceItemCategory>,
    experiences = EmptyArray as Array<App.ExperienceOffer>,
    isFetching = false,
  } = props

  const intl = useIntl()
  const dispatch = useAppDispatch()
  const { searchFilters, setSearchFilters } = useExperiencesSearchFilters()

  const [toLiftRef, liftInView] = useInView()

  const { collections } = useMostPopulatedSearchExperienceFacets(place)

  useEffect(() => {
    dispatch(fetchExperienceCategories())
  }, [dispatch])

  const onCategoryChange = useCallback((e: React.ChangeEvent<HTMLInputElement>) => {
    const category = categories.find(cat => cat.id === parseInt(e.currentTarget.value))

    if (category) {
      const selectedCategories = searchFilters.categoryCodes ?? []
      let nextLuxPlusFeatures = searchFilters.luxPlusFeatures

      let nextCategories = [...selectedCategories]
      if (!category.parentId || selectedCategories.includes(category.parentId)) {
        const otherChildCategories = categories.filter(cat => cat.parentId === category.parentId && cat.id !== category.id)
        nextCategories = otherChildCategories.map(cat => cat.id)
      } else if (selectedCategories.includes(category.id)) {
        nextCategories = without(nextCategories, category.id)
      } else {
        if (category.id === EXP_CATEGORY_LUX_PLUS_MEMBER_PRICES_ID) {
          if (nextLuxPlusFeatures?.includes('Member prices')) {
            nextLuxPlusFeatures = undefined
          } else {
            nextLuxPlusFeatures = ['Member prices']
          }
        } else {
          nextCategories.push(category.id)
        }
      }

      setSearchFilters({
        ...searchFilters,
        categoryCodes: nextCategories,
        luxPlusFeatures: nextLuxPlusFeatures,
      })
    }
  }, [categories, searchFilters, setSearchFilters])

  // Calculate counts based on current experiences (that respect bounds)
  const categoryCountsFromCurrentExperiences = useMemo(() => {
    const counts: Record<number, number> = {}
    experiences.forEach(experience => {
      experience.categories.forEach(cat => {
        counts[cat.id] = (counts[cat.id] || 0) + 1
      })
    })
    return counts
  }, [experiences])

  const categoryItems = useMemo((): Array<CategoryItem> => {
    const categoriesWithoutAnything = categories.filter(cat => cat.id !== AnythingCategory.id)
    const childCategoriesByParent = groupBy(
      categoriesWithoutAnything,
      cat => cat.parentId,
    )
    const topLevelCategories = categoriesWithoutAnything.filter(cat => cat.level === 'parent')
    const selectedCategories = searchFilters.categoryCodes ?? []

    return topLevelCategories.map((category) => {
      const isLuxPlus = category.id === EXP_CATEGORY_LUX_PLUS_ID
      const childCategories = childCategoriesByParent.get(category.id) ?? []

      // Calculate counts for child categories based on current experiences (bounds-aware)
      const childCategoriesWithCounts = childCategories.map(cat => ({
        ...cat,
        count: categoryCountsFromCurrentExperiences[cat.id] ?? 0,
      }))

      const childCategoriesWithExperiences = childCategoriesWithCounts.filter(cat => cat.count > 0)

      const sortedChildCategories = sortBy(childCategoriesWithExperiences, cat => cat.count, 'desc')
      const total = sum(sortedChildCategories, cat => cat.count)

      return {
        category: {
          ...category,
          name: isLuxPlus ? <LuxPlusLabel type="only-logo" /> : category.name,
        },
        count: total,
        items: sortedChildCategories.map((category): FilterPanelCheckItem => {
          const count = category.count

          const checked = isLuxPlus ? !!searchFilters.luxPlusFeatures?.length : selectedCategories.includes(category.id) || selectedCategories.includes(category.parentId ?? -1)

          return {
            label: <Group direction="horizontal" horizontalAlign="space-between">
              {category.name}
              <span>{intl.formatMessage({
                id: 'NptmiDOOuuFr29rUgQup',
                defaultMessage: '({count})',
                description: 'Category filter count in parentheses on experience search map',
              }, { count })}
              </span>
            </Group>,
            hidden: count === 0,
            value: category.id,
            checked,
          }
        }),
        isLuxPlus,
      }
    })
      .filter(val => (!val.isLuxPlus || isLuxPlusEnabled))
      .sort((a) => a.isLuxPlus ? -1 : 1)
  }, [categories, searchFilters.categoryCodes, isLuxPlusEnabled, searchFilters.luxPlusFeatures, categoryCountsFromCurrentExperiences, intl])

  const collectionItem = useMemo(() => {
    if (collections.length === 0) {
      return
    }

    const total = sum(collections, collection => collection.count)
    const selectedCollections = searchFilters.collections ?? []

    return {
      label: intl.formatMessage({
        id: 'QcxUdh79JsUuvsJ082T-',
        defaultMessage: 'Collections',
        description: 'Label for the collections filter dropdown in experience search map view',
      }),
      count: total,
      items: collections.map(collection => ({
        label: collection.theme,
        value: collection.collectionId,
        checked: selectedCollections.includes(collection.collectionId),
      })) ?? [],
    }
  }, [collections, searchFilters.collections, intl])

  const onResetCategory = useCallback((item: CategoryItem) => {
    const selectedCategories = searchFilters.categoryCodes ?? []
    const nextCategories = without(selectedCategories, item.category.id, ...item.items.map(i => i.value as number))
    setSearchFilters({
      ...searchFilters,
      categoryCodes: nextCategories,
      luxPlusFeatures: undefined,
    })
  }, [searchFilters, setSearchFilters])

  const onCollectionChange = useCallback((e: React.ChangeEvent<HTMLInputElement>) => {
    const collection = collectionItem?.items.find(item => item.value === e.currentTarget.value)
    if (collection) {
      const selectedCollections = searchFilters.collections ?? []
      let nextCollections = [...selectedCollections]
      if (selectedCollections.includes(collection.value)) {
        nextCollections = without(nextCollections, collection.value)
      } else {
        nextCollections.push(collection.value)
      }
      setSearchFilters({
        ...searchFilters,
        collections: nextCollections,
      })
    }
  }, [collectionItem, searchFilters, setSearchFilters])

  const onResetCollection = useCallback(() => {
    setSearchFilters({
      ...searchFilters,
      collections: [],
    })
  }, [searchFilters, setSearchFilters])

  return (
    <>
      <div ref={toLiftRef} />
      <HeaderContainer className={!liftInView ? 'lifted' : ''}>
        <FiltersContainer>
          {collectionItem && (
            <FilterDropdown
              label={collectionItem.label}
              items={collectionItem.items}
              onFilterChange={onCollectionChange}
              onReset={onResetCollection}
            />
          )}
          {categoryItems.map(item => (
            <CategoryFilterDropdown
              key={item.category.id}
              categoryItem={item}
              onCategoryChange={onCategoryChange}
              onResetCategory={onResetCategory}
              isFetching={isFetching}
            />
          ))}
        </FiltersContainer>
      </HeaderContainer>
    </>
  )
}

function mapStateToProps(state: App.State) {
  return {
    categories: state.experience.experienceCategories,
    isLuxPlusEnabled: getIsEnabledLuxPlusDiscountedExperiences(state),
  }
}

export default connect(mapStateToProps)(ExperienceSearchMapFilters)
